import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'

type NodeId = 'intent' | 'context' | 'evidence' | 'policy' | 'authority' | 'decision' | 'action-binding' | 'outcome'

type GraphNode = {
  id: NodeId
  title: string
  short: string
  meaning: string
  why: string
  ssf: string
  href: string
  x: number
  y: number
  width: number
  height: number
  tone?: 'amber' | 'cyan'
}

type GraphPath = {
  id: string
  from: NodeId
  to: NodeId
  d: string
  tone?: 'amber' | 'cyan'
}

const nodes: GraphNode[] = [
  { id: 'intent', title: 'Intent', short: 'Business objectives & desired outcomes', meaning: 'The authorised objective that defines what the system is trying to achieve.', why: 'Consequential decisions should be traceable to an explicit purpose, not generated simply because a system can act.', ssf: 'Intent integrity and decision purpose', href: '/research/semantic-security#intent', x: 194, y: 86, width: 172, height: 92, tone: 'cyan' },
  { id: 'context', title: 'Context', short: 'Real-time signals, systems & entities', meaning: 'The surrounding information needed to interpret a situation correctly.', why: 'A decision can be technically plausible and still be wrong when the relevant context is missing, stale or misunderstood.', ssf: 'Context security and context-aware reasoning', href: '/research/semantic-security#context', x: 194, y: 234, width: 172, height: 92, tone: 'cyan' },
  { id: 'evidence', title: 'Evidence', short: 'Verified facts with provenance', meaning: 'Facts, records, observations and signals used to support the decision.', why: 'Evidence should be attributable and consequence-aware so a decision can be explained and reconstructed later.', ssf: 'Evidence, provenance and trust chain', href: '/research/semantic-security#evidence', x: 194, y: 382, width: 172, height: 92, tone: 'cyan' },
  { id: 'policy', title: 'Policy', short: 'Rules, constraints & guardrails', meaning: 'The rules and constraints that determine what is permitted.', why: 'Recommendations from humans, applications or AI should not bypass organisational policy simply because they appear confident.', ssf: 'Governance, policy enforcement and control boundaries', href: '/research/semantic-security#policy', x: 436, y: 144, width: 172, height: 92, tone: 'amber' },
  { id: 'authority', title: 'Authority', short: 'Who can decide, based on what', meaning: 'The permission and delegated power to approve or execute a decision.', why: 'Intelligence is not authority. A good recommendation does not automatically confer permission to act.', ssf: 'Authority, delegation and accountability', href: '/research/semantic-security#authority', x: 436, y: 324, width: 172, height: 92, tone: 'amber' },
  { id: 'decision', title: 'Decision', short: 'Reasoned conclusion with confidence', meaning: 'The governed conclusion produced from intent, context, evidence and constraints.', why: 'The decision must remain attributable, inspectable and independently governable before it becomes executable.', ssf: 'Governed decision-making and semantic trust', href: '/research/semantic-security#decision', x: 640, y: 234, width: 184, height: 92, tone: 'cyan' },
  { id: 'action-binding', title: 'Action Binding', short: 'Execute with control, traceability & audit', meaning: 'The controlled capability that binds an authorised decision to the exact action allowed to occur.', why: 'Binding prevents an agent or application from changing target, scope or material parameters after authorisation.', ssf: 'Controlled execution and decision-to-action integrity', href: '/research/semantic-security#action-binding', x: 910, y: 234, width: 184, height: 92, tone: 'cyan' },
  { id: 'outcome', title: 'Outcome', short: 'Measured impact & continuous learning', meaning: 'The observed effect of an executed action.', why: 'Outcome evidence closes the accountability loop and can inform future decisions without automatically becoming authority or truth.', ssf: 'Outcomes, memory and accountable learning', href: '/research/semantic-security#outcome', x: 1140, y: 234, width: 136, height: 92, tone: 'cyan' },
]

const productCapabilities: Record<NodeId, { meaning: string; why: string; emphasis: string }> = {
  intent: { meaning: 'Capture the objective, owner, scope and success criteria for a consequential decision.', why: 'Conseqra gives every workflow an explicit operating intent before intelligence or automation begins.', emphasis: 'Objective and mandate capture' },
  context: { meaning: 'Assemble live signals, entities and operational state into a decision-ready view.', why: 'Teams and agents work from the same current context instead of fragmented application data.', emphasis: 'Live context assembly' },
  evidence: { meaning: 'Attach verified facts, records and provenance directly to the decision workflow.', why: 'Every recommendation and approval can be traced back to the evidence that supported it.', emphasis: 'Evidence and provenance trace' },
  policy: { meaning: 'Evaluate proposals against machine-enforceable policies, limits and control rules.', why: 'Policy checks become part of the workflow rather than a manual review after the event.', emphasis: 'Policy evaluation and guardrails' },
  authority: { meaning: 'Resolve who or what is permitted to approve a decision at its current level of consequence.', why: 'Delegated authority is explicit, inspectable and separate from the intelligence producing a recommendation.', emphasis: 'Delegated authority control' },
  decision: { meaning: 'Bring reasoning, confidence, evidence, exceptions and approvals into one operational decision workspace.', why: 'Operators can inspect, challenge and authorise a conclusion before it becomes executable.', emphasis: 'Governed decision workspace' },
  'action-binding': { meaning: 'Convert an approved decision into a bounded, tamper-resistant instruction for execution.', why: 'Targets, parameters and permissions remain locked to what was actually authorised.', emphasis: 'Controlled action binding' },
  outcome: { meaning: 'Measure execution results and preserve a complete decision-to-outcome record.', why: 'Conseqra closes the loop with operational evidence, auditability and accountable learning.', emphasis: 'Outcome observability' },
}

const paths: GraphPath[] = [
  { id: 'intent-policy', from: 'intent', to: 'policy', d: 'M366 132 C394 132 408 166 436 184' },
  { id: 'context-decision', from: 'context', to: 'decision', d: 'M366 280 C446 264 558 296 640 280' },
  { id: 'evidence-authority', from: 'evidence', to: 'authority', d: 'M366 428 C394 428 408 394 436 370' },
  { id: 'policy-decision', from: 'policy', to: 'decision', d: 'M608 190 C624 190 624 248 640 264', tone: 'amber' },
  { id: 'authority-decision', from: 'authority', to: 'decision', d: 'M608 370 C624 370 624 312 640 296', tone: 'amber' },
  { id: 'decision-action', from: 'decision', to: 'action-binding', d: 'M824 280 C852 264 882 296 910 280' },
  { id: 'action-outcome', from: 'action-binding', to: 'outcome', d: 'M1094 280 C1108 264 1126 296 1140 280' },
]

const sourceCards = [
  { y: 78, lines: ['MCP'], mark: 'mcp' },
  { y: 131, lines: ['OpenAI'], mark: 'openai' },
  { y: 184, lines: ['Anthropic'], logo: 'https://cdn.simpleicons.org/anthropic/D9E5F0' },
  { y: 237, lines: ['Salesforce'], logo: 'https://commons.wikimedia.org/wiki/Special:Redirect/file/Salesforce.com_logo.svg', logoTone: 'native', logoWidth: 24 },
  { y: 290, lines: ['Zscaler'], logo: 'https://commons.wikimedia.org/wiki/Special:Redirect/file/Zscaler_logo.png', logoTone: 'native', logoWidth: 30 },
  { y: 343, lines: ['Okta'], logo: 'https://cdn.simpleicons.org/okta/D9E5F0' },
  { y: 396, lines: ['JFrog'], logo: 'https://cdn.simpleicons.org/jfrog/D9E5F0' },
  { y: 449, lines: ['Microsoft Entra'], mark: 'microsoft' },
]

const outputCards = [
  { y: 160, framework: 'Permitted action', product: 'Bounded actions' },
  { y: 252, framework: 'System change', product: 'Connected systems' },
  { y: 344, framework: 'Evidence & memory', product: 'Outcome record' },
]

const ingressPaths = [
  'M144 97 C164 97 174 120 194 132',
  'M144 150 C164 150 174 230 194 254',
  'M144 203 C164 203 174 258 194 270',
  'M144 256 C164 256 174 274 194 278',
  'M144 309 C164 309 174 292 194 286',
  'M144 362 C164 362 174 390 194 406',
  'M144 415 C164 415 174 424 194 428',
  'M144 468 C164 468 174 440 194 432',
]

const egressPaths = [
  'M1276 280 C1294 280 1304 188 1324 188',
  'M1276 280 C1294 280 1304 280 1324 280',
  'M1276 280 C1294 280 1304 372 1324 372',
]

function GraphNodeShape({ node, active, related, onEnter, onLeave, onToggle }: {
  node: GraphNode
  active: boolean
  related: boolean
  onEnter: () => void
  onLeave: () => void
  onToggle: () => void
}) {
  const cx = node.x + node.width / 2
  const titleY = node.y + 40
  const words = node.short.split(' ')
  const lines: string[] = []
  let current = ''
  const limit = node.width > 185 ? 28 : 22
  words.forEach(word => {
    if ((current + ' ' + word).trim().length > limit) {
      lines.push(current)
      current = word
    } else current = (current + ' ' + word).trim()
  })
  if (current) lines.push(current)

  return (
    <g
      className={`graph-node graph-node--${node.tone ?? 'cyan'}${active ? ' is-active' : ''}${related ? ' is-related' : ''}`}
      role="button"
      tabIndex={0}
      aria-label={`${node.title}. ${node.short}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      onClick={(event) => { event.stopPropagation(); onToggle() }}
      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onToggle() } }}
    >
      <rect x={node.x} y={node.y} width={node.width} height={node.height} rx="18" />
      <circle className="graph-node__icon" cx={node.x + 24} cy={node.y + 31} r="7" />
      <text className="graph-node__title" x={node.x + 40} y={titleY}>{node.title.toUpperCase()}</text>
      <text className="graph-node__short" textAnchor="middle" x={cx} y={node.y + 67}>
        {lines.slice(0, 2).map((line, index) => <tspan key={line} x={cx} dy={index === 0 ? 0 : 15}>{line}</tspan>)}
      </text>
    </g>
  )
}

export function InteractiveDecisionGraph({ mode = 'framework' }: { mode?: 'framework' | 'product' }) {
  const [hovered, setHovered] = useState<NodeId | null>(null)
  const [pinned, setPinned] = useState<NodeId | null>(null)
  const [pulseIndex, setPulseIndex] = useState(0)
  const [detailPaused, setDetailPaused] = useState(false)
  const hoverTimer = useRef<number | null>(null)
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const cancelHoverClose = () => {
    if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current)
    hoverTimer.current = null
  }
  const showDetail = (id: NodeId) => {
    cancelHoverClose()
    setHovered(id)
  }
  const scheduleDetailClose = () => {
    cancelHoverClose()
    hoverTimer.current = window.setTimeout(() => setHovered(null), 900)
  }
  const detailId = pinned ?? hovered

  useEffect(() => {
    if (detailId || detailPaused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const timer = window.setInterval(() => setPulseIndex(index => (index + 1) % nodes.length), 4000)
    return () => window.clearInterval(timer)
  }, [detailId, detailPaused])

  useEffect(() => {
    if (mode !== 'product' || !window.matchMedia('(max-width: 760px)').matches) return
    const centerCanvas = () => {
      const canvas = canvasRef.current
      if (canvas) canvas.scrollLeft = Math.max(0, (canvas.scrollWidth - canvas.clientWidth) / 2)
    }
    const frame = window.requestAnimationFrame(centerCanvas)
    const timer = window.setTimeout(centerCanvas, 180)
    const observer = new ResizeObserver(centerCanvas)
    if (canvasRef.current) observer.observe(canvasRef.current)
    window.addEventListener('resize', centerCanvas)
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(timer)
      observer.disconnect()
      window.removeEventListener('resize', centerCanvas)
    }
  }, [mode])

  const activeId = detailId ?? nodes[pulseIndex].id
  const activeNode = nodes.find(node => node.id === activeId) ?? nodes[5]
  const detailNode = detailId ? nodes.find(node => node.id === detailId) ?? null : null
  const infoStyle = detailNode ? {
    '--graph-info-x': `${detailNode.x >= 700 ? (detailNode.x / 1450) * 100 + 1 : ((detailNode.x + detailNode.width) / 1450) * 100 + 1}%`,
    '--graph-info-y': `${(detailNode.y >= 320 ? ((detailNode.y + detailNode.height) / 450) * 100 - 7 : ((detailNode.y + detailNode.height / 2 - 55) / 450) * 100) + (detailNode.id === 'intent' ? 2 : 0)}%`,
    '--graph-info-shift': detailNode.y >= 320 ? (detailNode.x >= 700 ? 'translate(-100%, -100%)' : 'translate(0, -100%)') : detailNode.x >= 700 ? 'translate(-100%, -50%)' : 'translate(0, -50%)',
  } as CSSProperties : undefined
  const detailPresentation = detailNode && mode === 'product' ? productCapabilities[detailNode.id] : null
  const activePresentation = mode === 'product' ? productCapabilities[activeNode.id] : null

  const relatedNodes = useMemo(() => {
    const set = new Set<NodeId>([activeId])
    paths.forEach(path => {
      if (path.from === activeId || path.to === activeId) {
        set.add(path.from)
        set.add(path.to)
      }
    })
    return set
  }, [activeId])

  return (
    <div className={`interactive-graph interactive-graph--${mode}`} aria-label="Interactive governed decision workflow">
      <div ref={canvasRef} className="interactive-graph__canvas" onClick={() => setPinned(null)}>
        <svg className="interactive-graph__svg" viewBox="0 55 1450 450" role="img" aria-label="MCP, agents, integrations and organisational systems flow into a governed decision model, producing controlled actions, connected-system changes and accountable outcome evidence.">
          <defs>
            <filter id="graph-glow-cyan" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="4" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
            <filter id="graph-glow-amber" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="3" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
            <filter id="graph-river-glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="7" /></filter>
            <linearGradient id="graph-river-cyan" x1="0" x2="1"><stop offset="0" stopColor="#168BFF" stopOpacity=".08" /><stop offset=".5" stopColor="#22D3EE" stopOpacity=".52" /><stop offset="1" stopColor="#168BFF" stopOpacity=".1" /></linearGradient>
            <linearGradient id="graph-river-amber" x1="0" x2="1"><stop offset="0" stopColor="#F4B942" stopOpacity=".08" /><stop offset=".5" stopColor="#F4B942" stopOpacity=".45" /><stop offset="1" stopColor="#22D3EE" stopOpacity=".08" /></linearGradient>
          </defs>
          <g className="graph-orbit" aria-hidden="true">
            <circle cx="732" cy="280" r="98" /><circle cx="732" cy="280" r="120" /><circle cx="732" cy="280" r="144" />
          </g>
          <g className="graph-edge-paths" aria-hidden="true">
            {[...ingressPaths, ...egressPaths].map((d, index) => <g className="graph-path graph-path--cyan" key={d}>
              <path className="graph-path__river" d={d} />
              <path className="graph-path__base" d={d} />
              <path className="graph-path__signal graph-path__signal--wide" d={d} style={{ animationDelay: `${index * -0.28}s` }} />
              <path className="graph-path__signal graph-path__signal--spark" d={d} style={{ animationDelay: `${index * -0.18}s` }} />
            </g>)}
          </g>
          <g className="graph-edge-cards graph-edge-cards--sources">
            <text className="graph-edge-heading" x="18" y="70">INPUT SOURCES</text>
            {sourceCards.map(card => <g className="graph-edge-card" key={card.y}>
              <rect x="18" y={card.y} width="126" height="38" rx="11" />
              {card.logo
                ? <image className={`graph-edge-logo${card.logoTone === 'native' ? ' graph-edge-logo--native' : ''}`} href={card.logo} x={30 - ((card.logoWidth ?? 14) - 14) / 2} y={card.y + 11} width={card.logoWidth ?? 14} height="16" preserveAspectRatio="xMidYMid meet" />
                : card.mark === 'mcp'
                  ? <path className="graph-edge-logo graph-edge-logo--mcp" transform={`translate(29 ${card.y + 10}) scale(.72)`} d="M15.688 2.343a2.588 2.588 0 00-3.61 0l-9.626 9.44a.863.863 0 01-1.203 0 .823.823 0 010-1.18l9.626-9.44a4.313 4.313 0 016.016 0 4.116 4.116 0 011.204 3.54 4.3 4.3 0 013.609 1.18l.05.05a4.115 4.115 0 010 5.9l-8.706 8.537a.274.274 0 000 .393l1.788 1.754a.823.823 0 010 1.18.863.863 0 01-1.203 0l-1.788-1.753a1.92 1.92 0 010-2.754l8.706-8.538a2.47 2.47 0 000-3.54l-.05-.049a2.588 2.588 0 00-3.607-.003l-7.172 7.034-.002.002-.098.097a.863.863 0 01-1.204 0 .823.823 0 010-1.18l7.273-7.133a2.47 2.47 0 00-.003-3.537z" />
                : card.mark === 'openai'
                  ? <path className="graph-edge-logo graph-edge-logo--openai" transform={`translate(29 ${card.y + 10}) scale(.8)`} d="M11.248 18.25q-.825 0-1.568-.314a4.3 4.3 0 0 1-1.32-.874 4 4 0 0 1-1.304.214 4 4 0 0 1-2.046-.544 4.27 4.27 0 0 1-1.518-1.485 4 4 0 0 1-.56-2.095q0-.48.131-1.04A4.4 4.4 0 0 1 2.04 10.71a4.07 4.07 0 0 1 .017-3.4 4.2 4.2 0 0 1 1.056-1.418 3.8 3.8 0 0 1 1.6-.842 3.9 3.9 0 0 1 .76-1.683q.593-.759 1.451-1.188a4.04 4.04 0 0 1 1.832-.429q.825 0 1.567.313.742.314 1.32.875a4 4 0 0 1 1.304-.215q1.106 0 2.046.545a4.14 4.14 0 0 1 1.501 1.485q.578.941.578 2.095 0 .48-.132 1.04.66.61 1.023 1.419.363.792.363 1.666 0 .892-.38 1.717a4.3 4.3 0 0 1-1.072 1.435 3.8 3.8 0 0 1-1.584.825 3.8 3.8 0 0 1-.775 1.683 4.06 4.06 0 0 1-1.436 1.188 4.04 4.04 0 0 1-1.832.429m-4.076-2.062q.825 0 1.435-.347l3.103-1.782a.36.36 0 0 0 .164-.313v-1.42L7.881 14.62a.67.67 0 0 1-.726 0l-3.118-1.798a.5.5 0 0 1-.017.115v.198q0 .841.396 1.551.413.693 1.139 1.089a3.2 3.2 0 0 0 1.617.412m.165-2.69a.4.4 0 0 0 .181.05q.083 0 .165-.05l1.238-.71-3.977-2.31a.7.7 0 0 1-.363-.643v-3.58q-.825.362-1.32 1.122a2.9 2.9 0 0 0-.495 1.65q0 .809.413 1.55.412.743 1.072 1.123zm3.91 3.663q.875 0 1.585-.396a2.96 2.96 0 0 0 1.534-2.64v-3.564a.32.32 0 0 0-.165-.297l-1.254-.726v4.604a.7.7 0 0 1-.363.643l-3.119 1.799a3 3 0 0 0 1.783.577m.627-6.039V8.878L10.01 7.822 8.129 8.878v2.244l1.881 1.056zM7.057 5.859a.7.7 0 0 1 .363-.644l3.119-1.798a3 3 0 0 0-1.782-.578q-.874 0-1.584.396A2.96 2.96 0 0 0 6.05 4.324a3.07 3.07 0 0 0-.396 1.551v3.547q0 .199.165.314l1.237.726zm8.383 7.887q.825-.364 1.303-1.123.495-.758.495-1.65a3.15 3.15 0 0 0-.412-1.55q-.413-.743-1.073-1.123l-3.086-1.782q-.099-.065-.181-.049a.3.3 0 0 0-.165.05l-1.238.692 3.993 2.327a.6.6 0 0 1 .264.264.64.64 0 0 1 .1.363zm-3.317-8.382a.63.63 0 0 1 .726 0l3.135 1.831v-.297q0-.792-.396-1.501a2.86 2.86 0 0 0-1.105-1.155q-.71-.43-1.65-.43-.825 0-1.436.347L8.294 5.941a.36.36 0 0 0-.165.314v1.418z" />
                : card.mark === 'microsoft'
                  ? <g className="graph-edge-logo graph-edge-logo--microsoft" transform={`translate(30 ${card.y + 12}) scale(.875)`}>
                      <rect x="0" y="0" width="7" height="7" style={{ fill: '#F25022' }} />
                      <rect x="9" y="0" width="7" height="7" style={{ fill: '#7FBA00' }} />
                      <rect x="0" y="9" width="7" height="7" style={{ fill: '#00A4EF' }} />
                      <rect x="9" y="9" width="7" height="7" style={{ fill: '#FFB900' }} />
                    </g>
                  : <circle cx="37" cy={card.y + 19} r="5" />}
              <text x="52" y={card.y + 23}>{card.lines.map((line, index) => <tspan key={line} x="52" dy={index === 0 ? 0 : 11}>{line}</tspan>)}</text>
            </g>)}
          </g>
          <g className="graph-edge-cards graph-edge-cards--outputs">
            <text className="graph-edge-heading" x="1302" y="146">CONTROLLED OUTPUTS</text>
            {outputCards.map(card => <g className="graph-edge-card graph-edge-card--output" key={card.y}>
              <rect x="1324" y={card.y} width="112" height="56" rx="14" />
              <circle cx="1340" cy={card.y + 28} r="5" />
              <text x="1352" y={card.y + 25}>
                {(mode === 'product' ? card.product : card.framework).split(' ').map((word, index, words) => index === words.length - 1 && words.length > 1
                  ? <tspan key={word} x="1352" dy={index === 0 ? 0 : 13}>{word}</tspan>
                  : index === 0 ? <tspan key={word}>{word}</tspan> : <tspan key={word}> {word}</tspan>)}
              </text>
            </g>)}
          </g>
          <g className="graph-paths" aria-hidden="true">
            {paths.map(path => {
              const related = path.from === activeId || path.to === activeId
              return <g key={path.id} className={`${related ? 'is-active ' : ''}graph-path graph-path--${path.tone ?? 'cyan'}`}>
                <path className="graph-path__river" d={path.d} />
                <path className="graph-path__base" d={path.d} />
                <path className="graph-path__signal graph-path__signal--wide" d={path.d} />
                <path className="graph-path__signal graph-path__signal--spark" d={path.d} />
              </g>
            })}
          </g>
          {nodes.map(node => <GraphNodeShape
            key={node.id}
            node={node}
            active={node.id === activeId}
            related={relatedNodes.has(node.id)}
            onEnter={() => showDetail(node.id)}
            onLeave={scheduleDetailClose}
            onToggle={() => setPinned(current => current === node.id ? null : node.id)}
          />)}
        </svg>
        {detailNode && <div className="graph-info" style={infoStyle} aria-live="polite" onMouseEnter={cancelHoverClose} onMouseLeave={scheduleDetailClose} onClick={(event) => event.stopPropagation()}>
          <div className="graph-info__topline"><span>{detailNode.title}</span><span>{pinned === detailNode.id ? 'Pinned' : mode === 'product' ? 'Product capability' : 'SSF concept'}</span></div>
          <p className="graph-info__meaning">{detailPresentation?.meaning ?? detailNode.meaning}</p>
          <div className="graph-info__ssf"><span>{mode === 'product' ? 'Capability' : 'Framework focus'}</span><strong>{detailPresentation?.emphasis ?? detailNode.ssf}</strong></div>
          <a href={mode === 'product' ? '/platform' : detailNode.href}>{mode === 'product' ? 'Explore platform capabilities' : 'Explore in Semantic Security'} <span aria-hidden="true">→</span></a>
        </div>}
      </div>

      <div className="interactive-graph__mobile">
         <div className="graph-mobile-grid">
          {nodes.map(node => <button key={node.id} className={node.id === activeId ? 'graph-mobile-node is-active' : 'graph-mobile-node'} onClick={() => setPinned(node.id)}>
            <strong>{node.title}</strong><span>{node.short}</span>
          </button>)}
        </div>
        <div className="graph-mobile-detail" onMouseEnter={() => setDetailPaused(true)} onMouseLeave={() => setDetailPaused(false)} onFocusCapture={() => setDetailPaused(true)} onBlurCapture={() => setDetailPaused(false)}>
          <span className="eyebrow">{mode === 'product' ? 'Product capability' : 'SSF concept'}</span>
          <h3>{activeNode.title}</h3>
          <p>{activePresentation?.meaning ?? activeNode.meaning}</p>
          <p className="muted">{activePresentation?.why ?? activeNode.why}</p>
          <div className="graph-mobile-detail__focus"><span>{mode === 'product' ? 'Capability' : 'Framework focus'}</span><strong>{activePresentation?.emphasis ?? activeNode.ssf}</strong></div>
          <a href={mode === 'product' ? '/platform' : activeNode.href}>{mode === 'product' ? 'Explore platform capabilities' : 'Explore in Semantic Security'} →</a>
        </div>
      </div>
    </div>
  )
}
